import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getProject } from '../services/api';
import { HiOutlineViewGrid } from 'react-icons/hi';
import { FiCheckCircle, FiXCircle, FiAlertTriangle, FiClock, FiMinusCircle } from 'react-icons/fi';

const STATUS_ICONS = {
  NT: { icon: FiMinusCircle, color: 'var(--gray-400)' },
  EC: { icon: FiClock, color: 'var(--info)' },
  OK: { icon: FiCheckCircle, color: 'var(--green-600)' },
  KO: { icon: FiXCircle, color: 'var(--danger)' },
  BL: { icon: FiAlertTriangle, color: 'var(--warning)' },
};

export default function TraceabilityMatrix() {
  const { id } = useParams();
  const [project, setProject] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await getProject(id);
      setProject(data);
    } catch { /* handled by 401 interceptor */ }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!project) return (
    <div className="loading-spinner">
      <output className="spinner-border"></output>
      <span>Chargement...</span>
    </div>
  );

  const totalReqs = project.requirements.length;
  const coveredReqs = project.requirements.filter(r => r.test_cases.length > 0).length;
  const coveragePct = totalReqs > 0 ? ((coveredReqs / totalReqs) * 100).toFixed(0) : 0;
  const totalTC = project.requirements.reduce((s, r) => s + r.test_cases.length, 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><HiOutlineViewGrid className="me-2" style={{ color: 'var(--info)' }} />Matrice de Traçabilité</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>{project.name}</p>
        </div>
        <div className="actions">
          <div className="coverage-indicator">
            <div className="coverage-number">{coveragePct}%</div>
            <div className="coverage-label">Couverture</div>
          </div>
        </div>
      </div>
      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="stat-card">
            <div className="stat-icon stat-icon-navy" style={{ fontSize: '0.85rem', fontWeight: 700 }}>{totalReqs}</div>
            <div className="stat-content">
              <div className="stat-number">{coveredReqs}/{totalReqs}</div>
              <div className="stat-label">Exigences couvertes</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="stat-card">
            <div className="stat-icon stat-icon-green" style={{ fontSize: '0.85rem', fontWeight: 700 }}>{totalTC}</div>
            <div className="stat-content">
              <div className="stat-number">{totalTC}</div>
              <div className="stat-label">Cas de test total</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="stat-card">
            <div className="stat-icon stat-icon-warning" style={{ fontSize: '0.85rem', fontWeight: 700 }}>{totalReqs - coveredReqs}</div>
            <div className="stat-content">
              <div className="stat-number">{totalReqs - coveredReqs}</div>
              <div className="stat-label">Exigences sans test</div>
            </div>
          </div>
        </div>
      </div>

      {/* Matrix Table */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th style={{ width: '100px' }}>Exigence</th>
                <th>Description</th>
                <th style={{ width: '90px' }}>Type</th>
                <th style={{ width: '80px' }}>Impact</th>
                <th style={{ width: '50px' }}>CT</th>
                <th>Statuts des Tests</th>
                <th style={{ width: '110px' }}>Couverture</th>
              </tr>
            </thead>
            <tbody>
              {project.requirements.map(req => {
                const tcCount = req.test_cases.length;
                const okCount = req.test_cases.filter(tc => tc.statut === 'OK').length;
                const pct = tcCount > 0 ? ((okCount / tcCount) * 100).toFixed(0) : 0;
                const hasTests = tcCount > 0;

                return (
                  <tr key={req.id} className={!hasTests ? 'trace-no-coverage' : ''}>
                    <td><span className="req-id">{req.exg_id}</span></td>
                    <td className="small">{req.exigence}</td>
                    <td><span className="meta-tag">{req.type_exigence}</span></td>
                    <td><span className="meta-tag">{req.impact}</span></td>
                    <td className="fw-bold text-center">{tcCount}</td>
                    <td>
                      <div className="trace-status-dots">
                        {req.test_cases.map(tc => {
                          const cfg = STATUS_ICONS[tc.statut];
                          const Icon = cfg.icon;
                          return (
                            <span key={tc.id} className="trace-dot" title={`${tc.ct_id}: ${tc.cas_test}`}
                                  style={{ color: cfg.color }}>
                              <Icon />
                            </span>
                          );
                        })}
                        {!hasTests && <span className="text-muted small">Aucun test</span>}
                      </div>
                    </td>
                    <td>
                      {hasTests ? (
                        <div className="trace-coverage">
                          <div className="trace-coverage-bar">
                            <div className="trace-coverage-fill" style={{ width: `${pct}%` }}></div>
                          </div>
                          <span className="trace-coverage-text">{pct}%</span>
                        </div>
                      ) : (
                        <span className="text-danger small fw-500">Non couvert</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {totalReqs === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon"><HiOutlineViewGrid /></div>
          <p>Aucune exigence dans ce projet.</p>
        </div>
      )}
    </div>
  );
}
